/**
 * Shared service → {name, teamId, teamName} resolution.
 *
 * Building name/team lookups from `Service`/`Team` entities is needed by both
 * the Assigned Services table (issue #495 — resolve names + filter by team
 * for every assigned service, not just services the viewer owns) and the
 * project progress chart's team segmentation (issue #494). Keeping the map
 * construction as a pure function here lets both consumers share one
 * implementation instead of duplicating the lookup logic.
 */

import type { Service, Team } from '../graphql/types';

export interface ServiceResolutionEntry {
  serviceId: string;
  name: string;
  /**
   * Human-facing short identifier (issue #647 — Projects export "Surrogate
   * ID" column). Falls back to an empty string for services fetched via a
   * query that doesn't select `shortName`, so callers can still render a
   * dash instead of crashing.
   */
  shortName: string;
  teamId: string | null;
  teamName: string | null;
  /**
   * Tag entityIds applied to this service via `TAGGED_WITH` (issue #647 —
   * resolves Three Rs Key / Hosting Environment / user-selected tag-group
   * export columns). Empty array when the service has no tags or the source
   * query didn't select `tags`.
   */
  tagIds: string[];
}

export type ServiceResolutionMap = Map<string, ServiceResolutionEntry>;

export interface TeamOption {
  teamId: string;
  teamName: string;
}

/**
 * Build a serviceId -> {name, teamId, teamName} lookup from the full list of
 * services and teams. Services with no matching team still get an entry
 * (teamName null) so callers can distinguish "no team" from "unresolved".
 */
export function buildServiceResolutionMap(
  services: Service[],
  teams: Team[]
): ServiceResolutionMap {
  const teamNameById = new Map(teams.map((t) => [t.entityId, t.name]));
  const map: ServiceResolutionMap = new Map();
  for (const service of services) {
    const teamId = service.teamId ? service.teamId : null;
    map.set(service.entityId, {
      serviceId: service.entityId,
      name: service.name,
      shortName: service.shortName ?? '',
      teamId,
      teamName: teamId ? (teamNameById.get(teamId) ?? null) : null,
      tagIds: service.tags ?? [],
    });
  }
  return map;
}

/**
 * Resolve a display name for a service id, falling back to a truncated id
 * when the id is not present in the resolution map (e.g. the service was
 * deleted, or the caller only has a partial map).
 */
export function resolveServiceDisplayName(
  serviceId: string,
  resolutionMap: ServiceResolutionMap
): string {
  const entry = resolutionMap.get(serviceId);
  if (entry) return entry.name;
  return serviceId.slice(0, 16) + (serviceId.length > 16 ? '…' : '');
}

/**
 * Resolve the owning team id for a service, or null when unknown.
 */
export function resolveServiceTeamId(
  serviceId: string,
  resolutionMap: ServiceResolutionMap
): string | null {
  return resolutionMap.get(serviceId)?.teamId ?? null;
}

/**
 * Build a serviceId -> teamId lookup from the resolution map (issue #712).
 * Shared by `AssignedServicesTable`'s team filter and the Project Detail
 * route's Export-filtering pass so both apply an identical team predicate.
 */
export function buildServiceTeamIdMap(
  resolutionMap: ServiceResolutionMap
): Map<string, string | null> {
  return new Map([...resolutionMap.entries()].map(([id, entry]) => [id, entry.teamId]));
}

/**
 * Build a serviceId -> name lookup from the resolution map (issue #712),
 * for callers that only have `ServiceResolutionMap` (no deprecated
 * `serviceNames` fallback to merge in).
 */
export function buildServiceNameMapFromResolution(
  resolutionMap: ServiceResolutionMap
): Map<string, string> {
  return new Map([...resolutionMap.entries()].map(([id, entry]) => [id, entry.name]));
}

/**
 * Distinct, name-sorted list of teams referenced by the resolution map.
 * Used to populate a team filter dropdown.
 */
export function distinctTeamOptions(resolutionMap: ServiceResolutionMap): TeamOption[] {
  const byId = new Map<string, string>();
  for (const entry of resolutionMap.values()) {
    if (entry.teamId && !byId.has(entry.teamId)) {
      byId.set(entry.teamId, entry.teamName ?? entry.teamId);
    }
  }
  return [...byId.entries()]
    .map(([teamId, teamName]) => ({ teamId, teamName }))
    .sort((a, b) => a.teamName.localeCompare(b.teamName));
}
